import * as React from 'react'

import { cn, definedStyles } from './cn'

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  /** MUI spacing units (1 unit = 8px 4 gap). */
  spacing?: number
  alignItems?: React.CSSProperties['alignItems']
  justifyContent?: React.CSSProperties['justifyContent']
  flexWrap?: React.CSSProperties['flexWrap']
  flexGrow?: React.CSSProperties['flexGrow']
  flexShrink?: React.CSSProperties['flexShrink']
  alignSelf?: React.CSSProperties['alignSelf']
  display?: React.CSSProperties['display']
  flexDirection?: React.CSSProperties['flexDirection']
  overflow?: React.CSSProperties['overflow']
  /** MUI spacing units. */
  mt?: number
  mb?: number
  gap?: number | string
  width?: number | string
  height?: number | string
  useFlexGap?: boolean
  component?: React.ElementType
}

const directionClasses = {
  row: 'flex-row',
  'row-reverse': 'flex-row-reverse',
  column: 'flex-col',
  'column-reverse': 'flex-col-reverse',
}

const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'column',
      spacing = 0,
      alignItems,
      justifyContent,
      flexWrap,
      flexGrow,
      flexShrink,
      alignSelf,
      display,
      flexDirection,
      overflow,
      mt,
      mb,
      gap,
      width,
      height,
      useFlexGap,
      component: Component = 'div',
      className,
      style,
      ...props
    },
    ref,
  ) => (
    <Component
      ref={ref}
      className={cn('flex', directionClasses[direction], className)}
      style={definedStyles({
        // px strings: some React versions treat gap as unitless
        gap:
          gap !== undefined
            ? typeof gap === 'number'
              ? `${gap}px`
              : gap
            : spacing
              ? `${spacing * 8}px`
              : undefined,
        alignItems,
        justifyContent,
        flexWrap,
        flexGrow,
        flexShrink,
        alignSelf,
        flexDirection,
        overflow,
        marginTop: mt !== undefined ? mt * 8 : undefined,
        marginBottom: mb !== undefined ? mb * 8 : undefined,
        width,
        height,
        ...style,
      })}
      {...props}
    />
  ),
)
Stack.displayName = 'Stack'

export default Stack
